/**
 * Shared display formatters for alert/transaction primitives.
 *
 * Extracted in Fraud Hub PR 2 so the Critical Alerts Board can render
 * timestamps and asset tokens with the exact formatting the Fraud
 * Center already uses, without duplicating the helpers.
 */

/**
 * Render an ISO 8601 alert timestamp as `YYYY-MM-DD HH:MM` (UTC, second
 * precision dropped). Matches the dense-table style used in the Fraud
 * Center; investigators have learnt to scan this format.
 */
export function formatAlertTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace('T', ' ').slice(0, 16);
}

/**
 * Truncate an asset token (vehicle reg / card token / driver id) to
 * its first 8 characters for compact display in tables. Returns "—"
 * for null/empty input.
 */
export function shortToken(token: string | null): string {
  return token ? token.slice(0, 8) : '—';
}

/**
 * Canonical ZAR money formatter (QA-4) — space-grouped thousands, period
 * decimal, "R " prefix (e.g. `1234.56` -> `"R 1 234.56"`). Byte-for-byte
 * with the server's own R_ZAR unit convention (see
 * services/llm/chart_templates.py / sql_exemplars.yaml and
 * docs/superpowers/plans/2026-06-26-fuel-bench-charts-pii-allowlist.md
 * §"Server-owned unit formatters"). Extracted from `askAiFormatters.ts` so
 * any future ZAR display in the SPA has one place to import from instead
 * of re-implementing the grouping/decimal convention.
 */
function groupThousandsWithSpaces(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function formatRands(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const [intPart, decPart] = abs.toFixed(2).split('.');
  return `R ${sign}${groupThousandsWithSpaces(intPart)}.${decPart}`;
}
